<template>
  <div class="member-benefits-container">
    <h1 class="page-title">会员权益管理</h1>
    
    <!-- 权益列表卡片 -->
    <el-card class="content-card">
      <template #header>
        <div class="card-header">
          <h2>会员权益列表</h2>
          <el-button type="primary" @click="handleAddBenefit">
            <el-icon><Plus /></el-icon>
            添加权益
          </el-button>
        </div>
      </template>
      <div class="card-content">
        <!-- 搜索和筛选 -->
        <div class="search-filter-container">
          <el-form :inline="true" class="search-form">
            <el-form-item label="权益名称">
              <el-input v-model="searchForm.name" placeholder="请输入权益名称"></el-input>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleSearch">搜索</el-button>
              <el-button @click="handleReset">重置</el-button>
            </el-form-item>
          </el-form>
        </div>
        
        <!-- 权益列表 -->
        <el-table :data="benefitsList" style="width: 100%" v-loading="loading">
          <el-table-column prop="id" label="ID" width="80"></el-table-column>
          <el-table-column label="权益图标" width="120">
            <template #default="scope">
              <img 
                v-if="scope.row.icon" 
                :src="getIconUrl(scope.row.icon)" 
                class="table-icon" 
                :alt="scope.row.icon" 
              />
              <span v-else class="no-icon">无</span>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="权益名称" min-width="150"></el-table-column>
          <el-table-column prop="description" label="权益描述" min-width="200"></el-table-column>
          <el-table-column label="绑定优惠券" min-width="150">
            <template #default="scope">
              <el-tag v-if="scope.row.coupon_id" type="success" size="small">
                {{ getCouponName(scope.row.coupon_id) }}
              </el-tag>
              <span v-else class="no-coupon">未绑定</span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="200">
            <template #default="scope">
              <el-switch 
                v-model="scope.row.status" 
                active-text="启用" 
                inactive-text="禁用"
                @change="handleStatusChange(scope.row)"
              ></el-switch>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleEditBenefit(scope.row)">
                编辑
              </el-button>
              <el-button type="danger" size="small" @click="handleDeleteBenefit(scope.row)">
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            background
            layout="prev, pager, next, jumper, total"
            :total="total"
            :current-page="currentPage"
            :page-size="pageSize"
            @current-change="handleCurrentChange"
            @size-change="handleSizeChange"
          ></el-pagination>
        </div>
      </div>
    </el-card>
    
    <!-- 权益编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form ref="formRef" :model="formData" label-width="120px" :rules="rules">
        <el-form-item label="权益名称" prop="name">
          <el-input v-model="formData.name" placeholder="请输入权益名称"></el-input>
        </el-form-item>
        <el-form-item label="权益描述" prop="description">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="4"
            placeholder="请输入权益描述"
          ></el-input>
        </el-form-item>
        <el-form-item label="绑定优惠券">
          <el-select
            v-model="formData.coupon_id"
            placeholder="请选择要绑定的优惠券（非强制）"
            style="width: 100%"
            clearable
          >
            <el-option
              v-for="coupon in couponsList"
              :key="coupon.id"
              :label="coupon.title"
              :value="coupon.id"
            ></el-option>
          </el-select>
          <div class="form-tip">
            选择要绑定的优惠券，用户获得该权益时将自动获得绑定的优惠券
          </div>
        </el-form-item>
        <el-form-item label="权益图标">
          <div class="icon-selector">
            <div 
              v-for="icon in benefitIcons" 
              :key="icon.name"
              class="icon-item"
              :class="{ 'active': formData.icon === icon.name }"
              @click="formData.icon = icon.name"
            >
              <img :src="icon.url" :alt="icon.name" class="icon-image" />
              <span class="icon-name">{{ icon.name }}</span>
            </div>
          </div>
          <div class="form-tip">
            选择权益的图标，用于在会员中心展示
          </div>
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="formData.status" active-text="启用" inactive-text="禁用"></el-switch>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSave">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, onMounted, watch } from 'vue';
import { Plus } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import { 
  fetchBenefitList, 
  addBenefit, 
  updateBenefit, 
  deleteBenefit, 
  updateBenefitStatus,
  fetchBenefitIcons
} from '../../api/member';
import couponApi from '../../api/coupon.js';

// 初始数据加载
onMounted(() => {
  // 从后端加载数据
  console.log('会员权益管理页面已加载');
  fetchBenefits();
  fetchCoupons();
  fetchBenefitIconsList();
});

// 权益图标列表
const benefitIcons = ref([]);

// 获取权益图标列表
const fetchBenefitIconsList = async () => {
  try {
    // 只使用本地的5个 be_* 图片作为权益图标，与后端保持一致
    benefitIcons.value = [
      { name: 'be_1', url: require('@/assets/be_1.png') },
      { name: 'be_2', url: require('@/assets/be_2.png') },
      { name: 'be_3', url: require('@/assets/be_3.png') },
      { name: 'be_4', url: require('@/assets/be_4.png') },
      { name: 'be_5', url: require('@/assets/be_5.png') }
    ];
  } catch (error) {
    console.error('获取权益图标列表失败:', error);
    benefitIcons.value = [];
  }
};

// 优惠券列表
const couponsList = ref([]);

// 获取优惠券列表
const fetchCoupons = async () => {
  try {
    const response = await couponApi.getCoupons({ page: 1, per_page: 100 });
    if (response.code === 200 && response.data) {
      couponsList.value = response.data.data || [];
    } else if (response.success === true) {
      // 处理Laravel格式的响应
      couponsList.value = response.data || [];
    }
  } catch (error) {
    console.error('获取优惠券列表失败:', error);
  }
};

// 权益列表数据
const benefitsList = ref([]);

// 分页数据
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 搜索表单
const searchForm = reactive({
  name: '',
  type: ''
});

// 对话框相关
const dialogVisible = ref(false);
const dialogTitle = ref('添加权益');
const formData = reactive({
  id: '',
  name: '',
  description: '',
  coupon_id: '',
  status: true,
  icon: ''
});

// 加载状态
const loading = ref(false);

// 表单验证规则
const rules = {
  name: [
    { required: true, message: '请输入权益名称', trigger: 'blur' },
    { min: 2, max: 20, message: '权益名称长度在 2 到 20 个字符', trigger: 'blur' }
  ],
  description: [
    { required: true, message: '请输入权益描述', trigger: 'blur' }
  ]
};

// 表单引用
const formRef = ref(null);

// 获取优惠券名称
const getCouponName = (couponId) => {
  const coupon = couponsList.value.find(item => item.id === couponId);
  return coupon ? coupon.title : couponId;
};

// 根据图标名称获取完整URL
const getIconUrl = (iconName) => {
  const icon = benefitIcons.value.find(item => item.name === iconName);
  return icon ? icon.url : '';
};

// 获取权益列表
const fetchBenefits = async () => {
  loading.value = true;
  try {
    const params = {
      page: currentPage.value,
      per_page: pageSize.value,
      name: searchForm.name
    };
    const response = await fetchBenefitList(params);
    if (response.code === 200 && response.data) {
      // 将后端返回的整数类型 status 转换为布尔类型
      benefitsList.value = (response.data.data || []).map(item => ({
        ...item,
        status: !!item.status
      }));
      total.value = response.data.total || 0;
    }
  } catch (error) {
    console.error('获取会员权益列表失败:', error);
  } finally {
    loading.value = false;
  }
};

// 分页事件处理
const handleCurrentChange = (val) => {
  currentPage.value = val;
  fetchBenefits();
};

const handleSizeChange = (val) => {
  pageSize.value = val;
  currentPage.value = 1;
  fetchBenefits();
};

// 搜索和重置
const handleSearch = () => {
  currentPage.value = 1;
  fetchBenefits();
};

const handleReset = () => {
  searchForm.name = '';
  searchForm.type = '';
  currentPage.value = 1;
  fetchBenefits();
};

// 对话框操作
const handleAddBenefit = () => {
  dialogTitle.value = '添加权益';
  // 重置表单数据
  Object.assign(formData, {
    id: '',
    name: '',
    description: '',
    coupon_id: '',
    icon: '',
    status: true
  });
  if (formRef.value) {
    formRef.value.resetFields();
  }
  dialogVisible.value = true;
};

const handleEditBenefit = (row) => {
  dialogTitle.value = '编辑权益';
  // 复制行数据到表单
  Object.assign(formData, JSON.parse(JSON.stringify(row)));
  // 将整数类型的状态转换为布尔类型
  formData.status = !!row.status;
  // 设置coupon_id
  formData.coupon_id = row.coupon_id || '';
  // 设置icon
  formData.icon = row.icon || '';
  dialogVisible.value = true;
};

const handleDeleteBenefit = async (row) => {
  try {
    await deleteBenefit(row.id);
    ElMessage.success('删除会员权益成功');
    // 删除成功后重新获取数据
    fetchBenefits();
  } catch (error) {
    console.error('删除会员权益失败:', error);
    ElMessage.error('删除会员权益失败');
  }
};

const handleSave = async () => {
  if (!formRef.value) return;
  try {
    await formRef.value.validate();
    const data = {
      ...formData,
      // 将布尔类型的状态转换为整数类型（true -> 1, false -> 0）
      status: formData.status ? 1 : 0
    };
    
    if (formData.id) {
      // 编辑
      await updateBenefit(formData.id, data);
    } else {
      // 添加
      await addBenefit(data);
    }
    dialogVisible.value = false;
    // 保存成功后重新获取数据
    fetchBenefits();
  } catch (error) {
    console.error('保存会员权益失败:', error);
  }
};

// 状态变更
const handleStatusChange = async (row) => {
  try {
    // 将布尔类型的状态转换为整数类型（true -> 1, false -> 0）
    const statusInt = row.status ? 1 : 0;
    await updateBenefitStatus(row.id, statusInt);
    // 状态更新成功后重新获取数据
    fetchBenefits();
  } catch (error) {
    console.error('更新会员权益状态失败:', error);
    // 恢复原来的状态
    row.status = !row.status;
  }
};
</script>

<style scoped>
.member-benefits-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-content {
  padding: 20px 0;
}

.search-filter-container {
  margin-bottom: 20px;
}

.search-form {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 表单提示样式 */
.form-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* 未绑定优惠券样式 */
.no-coupon {
  color: #909399;
  font-size: 14px;
}

/* 图标选择器样式 */
.icon-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 10px;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 80px;
}

.icon-item:hover {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.icon-item.active {
  border-color: #67c23a;
  background-color: #f0f9eb;
}

.icon-image {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 5px;
}

.icon-name {
  font-size: 12px;
  color: #606266;
  text-align: center;
}

/* 表格图标样式 */
.table-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
  vertical-align: middle;
}

.no-icon {
  color: #909399;
  font-size: 14px;
}
</style>
